import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import _ from 'lodash';
import classNames from 'classnames';

import { combinationsValues, orderPosition } from 'src/utils/racingCombinations';
import { RootState, select } from 'src/configs/store';
import Icon from 'src/components/Icon/Icon';
import TypeCodes from '../TypeCodesModal/TypeCodes';
import DraggableDialog from 'src/components/DraggableDialog/DraggableDialog';
import SmartInput from 'src/SmartInput/SmartInput';

function HorsesFastMode() {
  const [showModal, setShowModal] = useState(false);
  const [focus, setFocus]= useState('cardCodes');
  const [cardCodes, setCardCodes] = useState([]);
  const [betCodes, setBetCodes] = useState([]);
  const { t } = useTranslation();
  const selectedSlip = useSelector(select.betslip.selectedSlip);
  const { retailErrors, retailValues } = useSelector((state: RootState) => state.racing);
  const { typeOfCode } = useSelector((state: RootState) => state.peripherals);
  const dispatch = useDispatch();

  const getCodes = async () => {
    const eventCodes = await dispatch.events.getEventCodes(2);
    const marketCodes = await dispatch.events.getBetCodes(3);
    if (eventCodes){
      setCardCodes(eventCodes);
    }

    if (marketCodes){
      setBetCodes(marketCodes);
    }
  };

  useEffect(() => {
    getCodes();
  }, []);

  const history = useHistory();

  const firstFocus = useRef(null);
  const secondFocus = useRef(null);
  const lastFocus = useRef(null);

  const getCombinations = async (eventCode, marketCode, orderedPositions, selectedSelectionType, selectedMarket, selectedHorsePosition) => {
    const payload = {
      eventCode: eventCode,
      marketCode: marketCode,
      marketOutcomeCode: selectedMarket.outcomes[0].code,
      line: 1,
      prediction: combinationsValues(orderedPositions, selectedSelectionType, selectedMarket, selectedHorsePosition),
      productType: 2
    };
    return await dispatch.lotto.permutations(payload);
  };

  const haveEmptyFields = () => {
    return _.isEmpty(retailValues.race)
    || _.isEmpty(retailValues.betCode)
    || _.isEmpty(retailValues.starters);
  };

  const handleChange = async (e) => {
    e.preventDefault();

    if (retailErrors[e.target.name] !== ''){
      dispatch.racing.changeErrors({ value:'', error: '' });
    }
    // if (e.target.value && e.target.name === 'betCode'){
    //   checkIfValidInput(e.target.value);
    // }

    await dispatch.racing.changeValues({ key: e.target.name, value: e.target.value });
  };

  const handleSumbit = async (e) => {
    e.preventDefault();
    if (_.isEmpty(retailValues.race) || _.isEmpty(retailValues.betCode) || _.isEmpty(retailValues.betType) || _.isEmpty(retailValues.starters)){
      return;
    }

    let race = retailValues.race;
    if (race === 'CR') {
      race = '0';
    }

    const eventInfo = await dispatch.events.fastInput({ productType: 2, race, couponCard: retailValues.betCode, betType: retailValues.betType });

    if (eventInfo?.error || !eventInfo){
      // Check if node retrieves error and stop if it has error.
      if (eventInfo?.error === 'INVALID_MARKET'){
        dispatch.racing.changeErrors({ value:'betType', error:t('racingDetail.invalidMarket') });
        history.push(`/racing/${ eventInfo.meetingInfos.meetingCode }/${ eventInfo.code }`);
      } else if (eventInfo?.error === 'INVALID_RACE'){
        dispatch.racing.changeErrors({ value: 'race', error:t('racingDetail.invalidRace') });
        history.push(`/racing/${ eventInfo.meetingInfos.meetingCode }/${ eventInfo.code }`);
      } else {
        dispatch.racing.changeErrors({ value:'betcode', error:t('racingDetail.invalidEvent') });
      }
      return;
    }

    let sportIcon = 'horses';
    if (eventInfo.sport.code === 204) sportIcon = 'dogs';
    else if (eventInfo.sport.code === 203) sportIcon = 'carriages';

    const market = eventInfo.markets.markets[0]; //grabbing market from the endPoint

    if (market.predictionCount === 1){
      const competitors = [];
      // If this is a single Market we just want to split every inserted horse to make a new Bet.
      const starters = retailValues.starters.match(new RegExp('.{1,' + 2 + '}', 'g'));

      for (const number of starters){
        const competitor = _.find(eventInfo.competitors, (competitor) => competitor.competitorId === +number);
        if (competitor){
          competitors.push({ ...competitor, startNo: +number });
        } else {
          // If user inserts invalid competitor we set an error.
          dispatch.racing.changeErrors({ value:'starters', error:t('racingDetail.invalidStarter') });
          return;
        }
      }

      if (selectedSlip.stakeSelection !== 'differentStake') {
        await dispatch.betslip.changeStakeSelection('differentStake');
      }
      //If everything is ok we loop through competitors so we can add one bet for each competitor.
      const bets = _.map(competitors, competitor => {
        const outcome = _.find(market.outcomes, { name: competitor.name }) || { value: 'TP', code: 0, name: competitor.name };

        return {
          eventCode: eventInfo.code,
          odds: outcome.value,
          marketOutcomeCode: outcome?.code || market.outcomes[0].code,
          marketCode: market.code,
          marketName: market.name,
          tournamentName: eventInfo.tournament.name,
          datetime:  eventInfo.scheduled * 1000,
          sport: 'racing',
          outcome: outcome?.name,
          eventName: eventInfo.tournament.name,
          genericName:market.name,
          horseType: 'FO',
          type: 'racing',
          country: eventInfo.tournamentGroup.name,
          productType: 2,
          jsonLabel: {
            id: competitor.progNo,
            competitor: competitor.outcomes ? competitor.outcomes[0]?.name : competitor.name
          },
          startNo: competitor.startNo,
          raceNo:  eventInfo.raceInfos.no,
          prediction: {
            numbersToPredictCount: market.predictionCount,
            anyNumbers: [competitor.startNo],
            rankedNumbers:[],
            inOrder: false
          },
          oddsType: 3,
          predictionCount: market.predictionCount,
          stake: +retailValues.stake || 1,
          racingType: sportIcon
        };
      });

      if (bets.length > 0) {
        //Add multiple bets from bets array
        dispatch.betslip.addBets(bets);
      }
    } else {
      const numbers = retailValues.starters.split('/');
      const horses = [...Array(market.predictionCount +1)].map(() => Array(0));
      const starters = _.map(numbers, number => number.match(new RegExp('.{1,' + 2 + '}', 'g')));

      if (starters.length > market.predictionCount + 1){
        dispatch.racing.changeErrors({ value:'starters', error:t('racingDetail.invalidStarter') });
        return;
      }

      for (let i =0; i<starters.length;i+=1){
        if (starters[i]){ // we don't want the nulls from regex but we want the indexes of null because of the all checkbox
          for (const number of starters[i]){
            const horse = eventInfo.competitors[+number];
            if (horse){
              horses[i].push(+number);
            } else {
              dispatch.racing.changeErrors({ value:'starters', error:t('racingDetail.invalidStarter') });
              return;
            }
          }
        }
      }

      let totalPositions = _.filter(horses, (position) => position.length > 0).length;
      if (totalPositions < market.predictionCount){
        totalPositions = horses[horses.length -1].length;
      }

      if (totalPositions < market.predictionCount && market.typeCode !== 10500){
        dispatch.racing.changeErrors({ value:'starters', error:t('racingDetail.invalidStarter') });
        return;
      }

      const orderedPosition = orderPosition(market.typeCode !== 10500 ? 'singlePerm' : null, market, horses);
      const prediction = combinationsValues(orderedPosition, 'singlePerm', market, horses);
      const combinations = await getCombinations(eventInfo.code, market.code, orderedPosition, 'singlePerm', market, horses);

      if (selectedSlip.stakeSelection !== 'differentStake') {
        await dispatch.betslip.changeStakeSelection('differentStake');
      }

      const bet = {
        eventCode:eventInfo.code,
        providerCode: 1,
        odds: 'TP',
        marketCode: market.code,
        marketOutcomeCode: market.outcomes[0].code,
        tournamentName: eventInfo.tournament.name,
        tournament: eventInfo.tournament,
        datetime: eventInfo.scheduled * 1000,
        eventName: eventInfo.tournament.name,
        sport: 'racing',
        outcome: market.isCrossEventMarket || market.typeCode === 10500 ? '' : 'Single/Perm',
        type:'racing',
        ...(market.isCrossEventMarket && { crossEvent: 'cross_event' }),
        orderedPosition,
        permutationsCount: combinations.bets[0].prediction.permutationsCount,
        marketName: market.name.toLowerCase(),
        marketDetails: market,
        prediction,
        country: eventInfo.tournamentGroup.name,
        jsonLabel: orderedPosition,
        horsePosition: horses,
        horseType: market.oddsType === 2 ? 'TP' : 'FO',
        productType: 2,
        raceNo:  eventInfo.raceInfos.no,
        selectionType:market.typeCode === 10500 ? '' : 'singlePerm',
        stake: +retailValues.stake || 1,
        repetitionsCount: market.isCrossEventMarket ? 2 : 1,
        racingType: sportIcon
      };

      await dispatch.betslip.addBet(bet);
    }
  };

  const tabFunction = (e) => {
    if (e.key === 'Tab' || e.keyCode === 9) {
      if (e.shiftKey) {
        firstFocus.current.focus();
        e.preventDefault();

      } else {
        if (document.activeElement === lastFocus.current) {
          firstFocus.current.focus();
          e.preventDefault();
        }
      }
    } else if (e.key === 'I' && e.shiftKey){
      e.preventDefault();
      setShowModal(true);
    } else if (e.key === 'Enter' && e.shiftKey){
      e.stopPropagation();
      e.preventDefault();
      dispatch.betslip.submitBets(true);
    }
  };

  useEffect(() => {
    addEventListener('keydown', tabFunction);
    return () => {
      removeEventListener('keydown', tabFunction);
    };
  }, []);

  const handleClick = (focusValue) => {
    setFocus(focusValue);
    setShowModal(!showModal);
  };

  const getTitle = () => {
    let title = t('racingDetail.retailBetCodesTitle');

    if (focus === 'cardCodes'){
      title = t('typeCodes.cardCodesHorseTitle');
    }

    return title;
  };

  const SmartInputSelect = useCallback((value, type) => {
    dispatch.racing.changeValues({ key: type, value });
  }, []);

  const checkOptions = (type) => {
    if (type === 'eventCodes'){
      const newCodes = _.map(cardCodes, (codes) => {
        if (typeOfCode === 'cardCode'){
          return { code: (codes.couponCardCode).toString(), name: codes.name };
        } else {
          return { code: (codes.couponCode).toString(), name: codes.name };
        }
      });
      return newCodes;
    } else {
      if (type === 'marketCodes'){
        const newCodes = _.map(betCodes, (codes) => {
          if (typeOfCode === 'cardCode'){
            return { code: (codes.betCardCode).toString(), name: codes.market_type.name };
          } else {
            return { code: (codes.betCode).toString(), name: codes.market_type.name };
          }
        });
        return newCodes;
      }
    }
    return null;
  };

  useEffect(() => {
    firstFocus.current.focus();
  }, [firstFocus]);

  return (
    <>
      <form onSubmit ={ handleSumbit } className="fast-mode__form fast-mode__form--horses" autoComplete="off">
        <div className="fast-mode__label fast-mode__underline" onClick={ () => handleClick('cardCodes') }>
          { t('betCodeSearch.cardCode') }
        </div>
        <div className="fast-mode__label fast-mode__underline" onClick={ () => handleClick('betCodes') }>
          { t('racingDetail.retailBetCodeLabel') }
        </div>
        <div className="fast-mode__label">
          { t('racingDetail.retailStarters') }
        </div>
        <div className="fast-mode__label">
          { t('racingDetail.retailRace') }
        </div>
        <div className="fast-mode__label">
          { t('racingDetail.retailStake') }
        </div>
        <div />
        <div>
          <SmartInput
            options={ checkOptions('eventCodes') }
            onSelect={ SmartInputSelect }
            className="fast-mode__search-input"
            ref={ firstFocus }
            name="betCode"
            onFocus={ () => setFocus('cardCodes') }
            placeHolder={ t(`typeOfCode.${ [typeOfCode] }`) }
          />
        </div>
        <div>
          <SmartInput
            options={ checkOptions('marketCodes') }
            onSelect={ SmartInputSelect }
            className="fast-mode__search-input"
            ref={ firstFocus }
            name="betType"
            onFocus={ () => setFocus('betCodes') }
            placeHolder={ typeOfCode === 'cardCode' ? t('numbers.betCodePlaceholder') : t('numbers.betCouponCodePlaceholder') }
          />
        </div>
        <input
          autoComplete="off"
          className={
            classNames({
              'fast-mode__input': true,
              'fast-mode__input--error': retailErrors.starters
            })
          }
          name= { 'starters' }
          value={ retailValues.starters }
          onChange={ handleChange }
        />
        <input
          autoComplete="off"
          ref={ secondFocus }
          className={
            classNames({
              'fast-mode__input': true,
              'fast-mode__input--error': retailErrors.race
            })
          }
          name="race"
          value={ retailValues.race }
          onChange={ handleChange }
        />
        <input
          className="fast-mode__input"
          ref={ lastFocus }
          name="stake"
          value={ retailValues.stake }
          onChange={ handleChange }
        />
        <button
          className={
            classNames({
              'fast-mode__button' : true,
              'fast-mode__button--disabled': haveEmptyFields()
            })
          }
          disabled = { haveEmptyFields() }
        >
          <Icon
            icon="return"
            className={
              classNames({
                'fast-mode__check': true,
                'fast-mode__check--disabled': haveEmptyFields()
              })
            }
          />
        </button>
        <span className="fast-mode__error">{ retailErrors.betcode }</span>
        <span className="fast-mode__error">{ retailErrors.betType }</span>
        <span className="fast-mode__error">{ retailErrors.starters }</span>
        <span className="fast-mode__error">{ retailErrors.race }</span>
        <span className="fast-mode__error">{ retailErrors.stake }</span>
      </form>
      {
        showModal &&
      <DraggableDialog className="fast-mode__dialog" placement="right-start">
        <DraggableDialog.Content>
          <TypeCodes
            closeModal = { () => setShowModal(false) }
            title= { getTitle() }
          >
            { focus === 'cardCodes' && <TypeCodes.CardCodes productType={ 2 } /> }
            { focus !== 'cardCodes' && <TypeCodes.HorseMarket /> }
          </TypeCodes>
        </DraggableDialog.Content>
      </DraggableDialog>
      }
    </>
  );
}

export default HorsesFastMode;
